import type {
  KnowledgeNotebookDocument,
  KnowledgeNoteDocument,
  KnowledgeTabDocument,
} from './note';

export const SYNC_ENTITY_TYPES = [
  'knowledge.tab',
  'knowledge.notebook',
  'knowledge.note',
  'profile.settings',
  'academics.course',
  'academics.timetable',
  'classroom.session',
  'review.mistake',
  'tasks.todo',
  'tasks.countdown',
  'planning.goal',
  'analytics.studyEvent',
] as const;

export type SyncEntityType = typeof SYNC_ENTITY_TYPES[number];

/**
 * 同步范围变化时强制重新拉取一次完整快照。
 * 旧游标只包含当时请求过的实体，单纯扩大 types 查询无法补回历史记录。
 */
export const SYNC_SCOPE_VERSION = '3';

export interface ProfileSettingsDocument {
  schemaVersion: 1;
  id: string;
  displayName: string;
  avatarSystemName: string;
  avatarColorHex: string;
  avatarImage?: { storageAssetID: string } | null;
  school: string;
  grade: string;
  streakDays: number;
  lastActiveDate: string;
  preferredLanguage: string;
  hapticEnabled: boolean;
  soundEnabled: boolean;
  dailyGoalMinutes: number;
  institutionBinding?: {
    institutionID: string;
    accountLabel: string;
    connectedAt: string;
    recordState: string;
  } | null;
  institutionBindingUpdatedAt?: string | null;
}

export interface AcademicCourseSchedule {
  id: string;
  weekday: number;
  startSlot: number;
  endSlot: number;
  location: string;
  weekPattern: string;
  notes: string;
  createdAt: string;
  reminderEnabled?: boolean;
  reminderMinutesBefore?: number;
  linkedTodoIDs?: string[];
}

export interface AcademicCourseDocument {
  schemaVersion: 1;
  id: string;
  name: string;
  subject: string;
  teacherName: string;
  colorHex: string;
  iconName: string;
  createdAt: string;
  updatedAt: string;
  isArchived: boolean;
  isPinned: boolean;
  sortOrder: number;
  notes: string;
  schedules: AcademicCourseSchedule[];
  semesterAcademicYearStart?: number;
  semesterTermRawValue?: 1 | 2;
}

export interface AcademicSemesterConfiguration {
  academicYearStart: number;
  term: 1 | 2;
  firstWeekStart: string;
  totalWeeks: number;
}

export interface AcademicTimetableDocument {
  schemaVersion: 1;
  id: string;
  slots: Array<{
    id: string;
    startMinutes: number;
    endMinutes: number;
    label: string;
  }>;
  semester?: AcademicSemesterConfiguration | null;
  semesterHistory?: AcademicSemesterConfiguration[];
  updatedAt: string;
}

export interface ClassroomTranscriptSegment {
  id: string;
  text: string;
  startTime: number;
  endTime: number;
  confidence: number;
  isFinalized: boolean;
  createdAt: string;
  speakerLabel: string;
  processingState: string;
  breakpointGroupID?: string | null;
  storageAssetID?: string | null;
}

export interface ClassroomKnowledgeBlock {
  id: string;
  title: string;
  summary: string;
  bulletPoints: string[];
  keywords: string[];
  sourceText: string;
  polishedTranscript: string;
  startTime: number;
  endTime: number;
  createdAt: string;
  orderIndex: number;
  processingStatus: string;
  retryCount: number;
  lastError?: string | null;
}

export type ClassroomQuestionType = 'singleChoice' | 'multipleChoice' | 'trueOrFalse' | 'fillBlank' | 'shortAnswer';

export interface ClassroomQuizQuestion {
  id: string;
  type: ClassroomQuestionType;
  stem: string;
  options: string[];
  correctAnswer: string;
  userAnswer: string;
  explanation: string;
  isAnsweredCorrectly?: boolean | null;
  orderIndex: number;
  answeredAt?: string | null;
}

export interface ClassroomQuiz {
  id: string;
  title: string;
  createdAt: string;
  difficulty: number;
  isCompleted: boolean;
  completedAt?: string | null;
  knowledgeBlockID?: string | null;
  questions: ClassroomQuizQuestion[];
}

export interface ClassroomSessionDocument {
  schemaVersion: 2;
  id: string;
  title: string;
  startedAt: string;
  endedAt?: string | null;
  pausedAt?: string | null;
  accumulatedPausedDuration?: number;
  summary: string;
  transcriptLanguage: string;
  isFinished: boolean;
  courseID?: string | null;
  transcriptSegments: ClassroomTranscriptSegment[];
  knowledgeBlocks: ClassroomKnowledgeBlock[];
  quizzes: ClassroomQuiz[];
}

export interface ReviewMistakeDocument {
  schemaVersion: 1;
  id: string;
  stem: string;
  options: string[];
  correctAnswer: string;
  lastUserAnswer: string;
  explanation: string;
  questionType: ClassroomQuestionType;
  firstWrongAt: string;
  lastWrongAt: string;
  wrongCount: number;
  correctRetryCount: number;
  lastReviewedAt?: string | null;
  mastery: number;
  note: string;
  questionID?: string | null;
  sessionID?: string | null;
  courseID?: string | null;
  knowledgeBlockID?: string | null;
  linkedNoteID?: string | null;
}

export interface TodoDocument {
  schemaVersion: 1;
  id: string;
  title: string;
  detail: string;
  isCompleted: boolean;
  completedAt?: string | null;
  dueAt?: string | null;
  createdAt: string;
  priority: number;
  reminderEnabled: boolean;
  courseID?: string | null;
}

export interface CountdownDocument {
  schemaVersion: 1;
  id: string;
  title: string;
  targetDate: string;
  emoji: string;
  colorHex: string;
  note: string;
  repeatRule: 'none' | 'yearly' | 'monthly' | 'weekly';
  sortOrder: number;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  courseID?: string | null;
}

export interface GoalMilestoneDocument {
  id: string;
  title: string;
  dueDate?: string | null;
  isCompleted: boolean;
  completedAt?: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface LearningGoalDocument {
  schemaVersion: 1;
  id: string;
  title: string;
  detail: string;
  successCriteria: string;
  nextAction: string;
  emoji: string;
  colorHex: string;
  startDate: string;
  targetDate?: string | null;
  priority: number;
  status: 'planned' | 'active' | 'paused' | 'completed';
  manualProgress: number;
  isArchived: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  courseID?: string | null;
  milestones: GoalMilestoneDocument[];
}

export interface StudyEventDocument {
  schemaVersion: 1;
  id: string;
  type: 'sessionStarted' | 'sessionEnded' | 'quizCompleted' | 'streakMilestone' | 'noteCreated';
  occurredAt: string;
  durationMinutes: number;
  detail: string;
}

export type SyncPayload =
  | KnowledgeTabDocument
  | KnowledgeNotebookDocument
  | KnowledgeNoteDocument
  | ProfileSettingsDocument
  | AcademicCourseDocument
  | AcademicTimetableDocument
  | ClassroomSessionDocument
  | ReviewMistakeDocument
  | TodoDocument
  | CountdownDocument
  | LearningGoalDocument
  | StudyEventDocument;

export interface CollaborationAccess {
  ownerUserId: string;
  role: 'viewer' | 'editor';
}

export interface ServerSyncRecord {
  entityType: SyncEntityType;
  entityId: string;
  schemaVersion: number;
  version: number;
  cursor: string;
  payload: SyncPayload | Record<string, never>;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  access?: CollaborationAccess;
}

export interface SyncMutationResult {
  mutationId: string;
  entityType: SyncEntityType;
  entityId: string;
  status: 'applied' | 'conflict';
  version: number;
  cursor?: string;
  merged?: boolean;
  reason?: string;
  conflictingPaths?: string[];
}
